import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileCheck, AlertTriangle, XCircle, Clock, Plus } from 'lucide-react';
import { useDemoContext } from '../../contexts/DemoContext';
import { formatDisplayDate } from '../../demo/demoUtils';

const emptyForm = {
  vendor_id: '',
  insurance_type_id: '',
  policy_name: '',
  policy_number: '',
  insurer_name: '',
  effective_date: '',
  expiration_date: '',
  general_liability_occurrence_limit: '',
  general_liability_aggregate_limit: '',
  workers_comp_limit: '',
  additional_insured_status: false,
};

export default function DemoPoliciesPage() {
  const {
    policies,
    vendors,
    insuranceTypes,
    createPolicy,
  } = useDemoContext();
  const [searchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const statusFilter = searchParams.get('status');
  const vendorFilter = searchParams.get('vendor');

  const selectedVendor = useMemo(() => {
    const vendorId = parseInt(vendorFilter || '', 10);
    if (Number.isNaN(vendorId)) {
      return null;
    }
    return vendors.find((vendor) => vendor.id === vendorId) || null;
  }, [vendorFilter, vendors]);

  const filteredPolicies = useMemo(() => {
    let filtered = policies;

    if (vendorFilter) {
      const vendorId = parseInt(vendorFilter, 10);
      if (!Number.isNaN(vendorId)) {
        filtered = filtered.filter((policy) => policy.vendor_id === vendorId);
      }
    }

    if (statusFilter === 'expiring') {
      filtered = filtered.filter((policy) => policy.is_expiring_soon);
    } else if (statusFilter === 'active') {
      filtered = filtered.filter((policy) => policy.status === 'active');
    } else if (statusFilter === 'expired') {
      filtered = filtered.filter((policy) => policy.status === 'expired');
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter((policy) => policy.status === 'pending');
    }

    return [...filtered].sort((left, right) => left.days_until_expiration - right.days_until_expiration);
  }, [policies, statusFilter, vendorFilter]);

  const getStatusBadge = (policy) => {
    if (policy.status === 'expired') {
      return {
        icon: XCircle,
        label: 'Expired',
        className: 'bg-red-100 text-red-800',
      };
    }
    if (policy.status === 'pending') {
      return {
        icon: Clock,
        label: 'Pending',
        className: 'bg-gray-100 text-gray-800',
      };
    }
    if (policy.is_expiring_soon) {
      return {
        icon: AlertTriangle,
        label: 'Expiring Soon',
        className: 'bg-yellow-100 text-yellow-800',
      };
    }
    return {
      icon: FileCheck,
      label: 'Active',
      className: 'bg-green-100 text-green-800',
    };
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Policies</h1>
          <p className="text-gray-600 mt-1">
            {selectedVendor
              ? `${filteredPolicies.length} policies for ${selectedVendor.name}`
              : `${filteredPolicies.length} policies`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500 bg-yellow-50 border border-yellow-200 px-4 py-2 rounded-lg">
            Demo Mode: Changes won't be saved
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Policy
          </button>
        </div>
      </div>

      {selectedVendor && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-900">
              Showing policies for {selectedVendor.name}
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Use Add Policy to create coverage for this vendor.
            </p>
          </div>
          <Link to="/demo/policies" className="text-sm font-medium text-blue-700 hover:text-blue-900">
            Clear vendor filter
          </Link>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        <FilterPill
          to={vendorFilter ? `/demo/policies?vendor=${vendorFilter}` : '/demo/policies'}
          active={!statusFilter}
        >
          All Policies
        </FilterPill>
        <FilterPill
          to={buildFilteredLink(vendorFilter, 'active')}
          active={statusFilter === 'active'}
        >
          Active
        </FilterPill>
        <FilterPill
          to={buildFilteredLink(vendorFilter, 'expiring')}
          active={statusFilter === 'expiring'}
        >
          Expiring Soon
        </FilterPill>
        <FilterPill
          to={buildFilteredLink(vendorFilter, 'pending')}
          active={statusFilter === 'pending'}
        >
          Pending
        </FilterPill>
        <FilterPill
          to={buildFilteredLink(vendorFilter, 'expired')}
          active={statusFilter === 'expired'}
        >
          Expired
        </FilterPill>
      </div>

      <div className="space-y-4">
        {filteredPolicies.map((policy) => {
          const statusInfo = getStatusBadge(policy);
          const StatusIcon = statusInfo.icon;

          return (
            <Link
              key={policy.id}
              to={`/demo/policies/${policy.id}`}
              className="card p-6 hover:shadow-md transition-shadow block"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {policy.policy_name || 'Policy'}
                    </h3>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${statusInfo.className}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusInfo.label}
                    </span>
                  </div>
                  <p className="text-gray-600">{policy.vendor_name}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {policy.insurance_type?.name || 'Policy'}
                    {policy.policy_number ? ` • #${policy.policy_number}` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Expires</p>
                  <p className={`font-medium ${
                    policy.status === 'expired'
                      ? 'text-red-600'
                      : policy.is_expiring_soon
                        ? 'text-yellow-600'
                        : policy.status === 'pending'
                          ? 'text-gray-600'
                          : 'text-gray-900'
                    }`}>
                    {formatDisplayDate(policy.expiration_date)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {policy.status === 'expired'
                      ? `Expired ${Math.abs(policy.days_until_expiration)} days ago`
                      : policy.status === 'pending'
                        ? `Starts ${formatDisplayDate(policy.effective_date)}`
                        : policy.days_until_expiration === 0
                          ? 'Expires today'
                          : `${policy.days_until_expiration} days left`}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Insurer</p>
                  <p className="font-medium text-gray-900">{policy.insurer_name || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-gray-500">GL Occurrence</p>
                  <p className="font-medium text-gray-900">
                    {formatCurrency(policy.general_liability_occurrence_limit)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Additional Insured</p>
                  <p className={`font-medium ${policy.additional_insured_status ? 'text-green-600' : 'text-gray-400'}`}>
                    {policy.additional_insured_status ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {filteredPolicies.length === 0 && (
        <div className="card p-12 text-center">
          <FileCheck className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">
            {selectedVendor
              ? `No policies found for ${selectedVendor.name}`
              : 'No policies found'}
          </p>
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add Policy
          </button>
        </div>
      )}

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>In Demo Mode:</strong> Add a policy with an upcoming expiration date to see how
          active, expiring soon, and expired states appear throughout the product.
        </p>
      </div>

      {showCreateModal && (
        <DemoPolicyModal
          vendors={vendors}
          insuranceTypes={insuranceTypes}
          defaultVendorId={selectedVendor?.id || null}
          onClose={() => setShowCreateModal(false)}
          onSave={(formData) => {
            createPolicy(formData);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

function FilterPill({ to, active, children }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-primary-100 text-primary-700'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {children}
    </Link>
  );
}

function buildFilteredLink(vendorFilter, status) {
  const params = new URLSearchParams();
  if (vendorFilter) {
    params.set('vendor', vendorFilter);
  }
  params.set('status', status);
  return `/demo/policies?${params.toString()}`;
}

function DemoPolicyModal({ vendors, insuranceTypes, defaultVendorId, onClose, onSave }) {
  const defaultInsuranceTypeId = insuranceTypes.find((type) => type.name === 'General Liability')?.id || insuranceTypes[0]?.id || null;
  const [formData, setFormData] = useState(() => ({
    ...emptyForm,
    vendor_id: defaultVendorId ? String(defaultVendorId) : '',
    insurance_type_id: defaultInsuranceTypeId ? String(defaultInsuranceTypeId) : '',
  }));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [customFields, setCustomFields] = useState([]);

  const activeVendors = useMemo(
    () => {
      const currentVendors = vendors.filter((vendor) => vendor.status === 'active');
      const selfVendor = currentVendors.find((vendor) => vendor.is_self);
      const otherVendors = currentVendors
        .filter((vendor) => !vendor.is_self)
        .sort((left, right) => left.name.localeCompare(right.name));

      return selfVendor ? [selfVendor, ...otherVendors] : otherVendors;
    },
    [vendors]
  );
  const fieldDefinitions = useMemo(() => (
    insuranceTypes.flatMap((insuranceType) =>
      (insuranceType.custom_fields || []).map((field) => ({
        id: field.id,
        name: field.field_name,
        field_name: field.field_name,
        data_type: field.field_type,
        field_type: field.field_type,
        insurance_type_name: insuranceType.name,
      }))
    )
  ), [insuranceTypes]);

  const updateCustomField = (index, updates) => {
    setCustomFields((previousFields) =>
      previousFields.map((field, currentIndex) => (currentIndex === index ? { ...field, ...updates } : field))
    );
  };
  const handleFieldSelection = (index, value) => {
    if (value === 'new') {
      updateCustomField(index, {
        mode: 'new',
        field_definition_id: '',
        name: '',
        data_type: 'text',
        value: '',
      });
      return;
    }

    if (!value) {
      updateCustomField(index, {
        mode: '',
        field_definition_id: '',
        name: '',
        data_type: 'text',
        value: '',
      });
      return;
    }

    const definition = fieldDefinitions.find((field) => field.id === parseInt(value, 10));
    updateCustomField(index, {
      mode: 'existing',
      field_definition_id: value,
      name: definition?.name || '',
      data_type: definition?.data_type || 'text',
      value: '',
    });
  };
  const addCustomField = () => {
    setCustomFields((previousFields) => [
      ...previousFields,
      { mode: '', field_definition_id: '', name: '', data_type: 'text', value: '' },
    ]);
  };
  const removeCustomField = (index) => {
    setCustomFields((previousFields) => previousFields.filter((_, currentIndex) => currentIndex !== index));
  };
  const renderValueInput = (field, index) => {
    const fieldType = field.data_type || 'text';
    if (fieldType === 'number') {
      return (
        <input
          type="number"
          value={field.value ?? ''}
          onChange={(event) => updateCustomField(index, { value: event.target.value })}
          className="input"
          placeholder="Enter number"
        />
      );
    }
    if (fieldType === 'date') {
      return (
        <input
          type="date"
          value={field.value ?? ''}
          onChange={(event) => updateCustomField(index, { value: event.target.value })}
          className="input"
        />
      );
    }
    if (fieldType === 'boolean') {
      return (
        <select
          value={field.value ?? ''}
          onChange={(event) => updateCustomField(index, { value: event.target.value })}
          className="input"
        >
          <option value="">Select</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }
    return (
      <input
        type="text"
        value={field.value ?? ''}
        onChange={(event) => updateCustomField(index, { value: event.target.value })}
        className="input"
        placeholder="Enter text"
      />
    );
  };
  const buildCustomFieldPayload = () => {
    const custom_field_definitions = [];
    const custom_field_values = {};
    let nextLocalFieldId = 1000;

    for (const field of customFields) {
      if (field.mode === 'existing') {
        if (!field.field_definition_id) {
          continue;
        }

        const definition = fieldDefinitions.find((item) => item.id === parseInt(field.field_definition_id, 10));
        if (!definition) {
          continue;
        }

        custom_field_definitions.push({
          id: definition.id,
          field_name: definition.field_name,
          field_type: definition.field_type,
          required: false,
        });
        custom_field_values[definition.id] = field.value;
      } else if (field.mode === 'new') {
        if (!field.name || !field.data_type) {
          return { error: 'Custom fields require a name and data type' };
        }

        const fieldId = nextLocalFieldId;
        nextLocalFieldId += 1;
        custom_field_definitions.push({
          id: fieldId,
          field_name: field.name.trim(),
          field_type: field.data_type,
          required: false,
        });
        custom_field_values[fieldId] = field.value;
      }
    }

    return { custom_field_definitions, custom_field_values };
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    if (!formData.vendor_id || !formData.policy_name.trim() || !formData.expiration_date) {
      setError('Vendor, policy name, and expiration date are required');
      return;
    }

    const { custom_field_definitions, custom_field_values, error: customFieldError } = buildCustomFieldPayload();
    if (customFieldError) {
      setError(customFieldError);
      return;
    }

    setError('');
    setSaving(true);

    try {
      onSave({
        vendor_id: parseInt(formData.vendor_id, 10),
        insurance_type_id: parseInt(formData.insurance_type_id, 10),
        policy_name: formData.policy_name.trim(),
        expiration_date: formData.expiration_date,
        custom_field_definitions,
        custom_field_values,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black opacity-30" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-semibold mb-6">Add Policy</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="label">Vendor *</label>
              <select
                value={formData.vendor_id}
                onChange={(event) => setFormData((previous) => ({ ...previous, vendor_id: event.target.value }))}
                className="input"
                required
              >
                <option value="">Select a vendor</option>
                {activeVendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.is_self ? 'Self (My Company)' : vendor.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Policy Name *</label>
              <input
                type="text"
                value={formData.policy_name}
                onChange={(event) => setFormData((previous) => ({ ...previous, policy_name: event.target.value }))}
                className="input"
                placeholder="Acme Insurance Policy 2026"
                required
              />
            </div>

            <div>
              <label className="label">Expiration Date *</label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(event) => setFormData((previous) => ({ ...previous, expiration_date: event.target.value }))}
                className="input"
                required
              />
            </div>

            <div>
              <label className="label">Document (optional)</label>
              <div className="input flex items-center gap-3 bg-gray-50 text-gray-500">
                <button
                  type="button"
                  disabled
                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-500"
                >
                  Not available for demo mode
                </button>
                <span className="text-sm">No file chosen</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Uploads are not available in demo mode.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label">Custom Fields (optional)</label>
                <button
                  type="button"
                  onClick={addCustomField}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Add Field
                </button>
              </div>
              {customFields.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded text-sm text-gray-500">
                  No custom fields yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {customFields.map((field, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <select
                          value={field.mode === 'new' ? 'new' : field.field_definition_id}
                          onChange={(event) => handleFieldSelection(index, event.target.value)}
                          className="input"
                        >
                          <option value="">Select field</option>
                          <option value="new">+ Create new field</option>
                          {fieldDefinitions.map((fieldDefinition) => (
                            <option key={fieldDefinition.id} value={fieldDefinition.id}>
                              {fieldDefinition.name} ({fieldDefinition.data_type})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeCustomField(index)}
                          className="text-sm text-gray-400 hover:text-red-600"
                        >
                          Remove
                        </button>
                      </div>

                      {field.mode === 'new' && (
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={field.name}
                            onChange={(event) => updateCustomField(index, { name: event.target.value })}
                            className="input"
                            placeholder="Field name"
                          />
                          <select
                            value={field.data_type}
                            onChange={(event) => updateCustomField(index, { data_type: event.target.value })}
                            className="input"
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="boolean">True/False</option>
                          </select>
                        </div>
                      )}

                      {field.mode && renderValueInput(field, index)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Create Policy'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
